import { Link, usePage, router } from "@inertiajs/react";
import { useState } from "react";
import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import MissingAccountFields from "@/components/settings/MissingAccountFields";
import AdjustmentForm from "@/components/adjustments/AdjustmentForm";
import TransferForm from "@/components/transfers/TransferForm";
import OrderCard from "@/components/orders/Order";
import Panel from "@/components/ui/Panel";
import Badge, { type BadgeTone } from "@/components/ui/Badge";
import Avatar from "@/components/ui/Avatar";
import Button, { ButtonAnchor, ButtonLink } from "@/components/ui/Button";
import EmptyState from "@/components/ui/EmptyState";
import Icon from "@/components/ui/Icon";
import { Checkbox } from "@/components/ui/Field";
import inviteIcon from "@/assets/icons/invite.svg";
import { ACCOUNT_REQUIRED_FIELDS, PrivateUser } from "@/interfaces/user";
import type { Order } from "@/interfaces/order";
import type { Item } from "@/interfaces/item";
import type { SharedProps } from "@/types";

type TimelineItem = {
  name: string;
  date: string;
  link?: string;
};

interface Props extends SharedProps {
  page_user: PrivateUser;
  custom: boolean;
  timeline: TimelineItem[];
  orders: (Order & { item: Item })[];
  [_: string]: unknown;
}

const PERMISSIONS = [
  "is_admin",
  "is_reviewer",
  "is_fulfiller",
  "is_debt",
  "can_ship_for_travel",
  "onboarding_completed",
] as const;
const PERMISSION_LABELS: Record<string, string> = {
  is_admin: "Admin",
  is_reviewer: "Reviewer",
  is_fulfiller: "Fulfiller",
  is_debt: "Debt",
  can_ship_for_travel: "Travel grant",
  onboarding_completed: "Onboarded",
};
const PERMISSION_HINTS: Record<string, string> = {
  can_ship_for_travel:
    "Lets this account open and ship projects with no Boba Bash attached - for staff coding towards a travel grant.",
};

// The roles are flat booleans on the user, so the badge row is the only place
// someone can see all of them at once.
function rolesFor(user: PrivateUser): { label: string; tone: BadgeTone }[] {
  const roles: { label: string; tone: BadgeTone }[] = [];

  if (user.is_admin) roles.push({ label: "HQ", tone: "brand" });
  if (user.is_organizer) roles.push({ label: "Organizer", tone: "info" });
  if (user.is_reviewer) roles.push({ label: "Reviewer", tone: "info" });
  if (user.is_fulfiller) roles.push({ label: "Fulfiller", tone: "info" });
  if (user.is_debt)
    roles.push({ label: "Exempt from ship stops", tone: "neutral" });
  if (user.can_overspend)
    roles.push({ label: "Can overspend", tone: "warning" });
  if (user.can_ship_for_travel)
    roles.push({ label: "Travel grant", tone: "warning" });

  return roles;
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-line/20 flex flex-wrap items-center justify-between gap-2 border-b py-2.5 last:border-b-0 last:pb-0">
      <span className="font-display text-caption text-ink-soft font-bold tracking-wide uppercase">
        {label}
      </span>
      <span className="text-body text-ink text-right break-all">
        {children}
      </span>
    </div>
  );
}

export default function UserPage() {
  const { props } = usePage<Props>();
  const pageUser = props.page_user;
  const missingFields = ACCOUNT_REQUIRED_FIELDS.filter(
    (f) => !pageUser[f as keyof PrivateUser],
  );

  // The ticket economy is switched off for Boba Bash and comes back as Pearls,
  // so these sections stay wired up but hide rather than report zeroes.
  const projectsOn = props.program.projects_enabled;
  const shopOn = props.program.shop_enabled;
  const economyOn = projectsOn || shopOn;

  const [permissions, setPermissions] = useState({
    is_admin: pageUser.is_admin,
    is_reviewer: pageUser.is_reviewer,
    is_fulfiller: pageUser.is_fulfiller,
    is_debt: pageUser.is_debt,
    can_ship_for_travel: pageUser.can_ship_for_travel,
    onboarding_completed: pageUser.onboarding_completed,
  });

  const permissionsChanged = PERMISSIONS.some(
    (perm) => permissions[perm] !== pageUser[perm],
  );

  function updatePermissions() {
    router.patch(`/admin/users/${pageUser.id}/permissions`, {
      is_admin: String(permissions.is_admin),
      is_reviewer: String(permissions.is_reviewer),
      is_fulfiller: String(permissions.is_fulfiller),
      is_debt: String(permissions.is_debt),
      can_ship_for_travel: String(permissions.can_ship_for_travel),
      onboarding_completed: String(permissions.onboarding_completed),
    });
  }

  const roles = rolesFor(pageUser);
  const transfers = [
    ...pageUser.outgoing_ticket_transfers.filter(
      (t) => t.aasm_state !== "rejected",
    ),
    ...pageUser.incoming_ticket_transfers.filter(
      (t) => t.aasm_state === "approved",
    ),
  ];

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          title={props.custom ? pageUser.username : "Settings"}
          subtitle={props.custom ? pageUser.email : undefined}
          actions={
            <ButtonLink
              href={`/users/${pageUser.id}`}
              variant="secondary"
              size="sm"
            >
              View public profile
            </ButtonLink>
          }
        />

        <MissingAccountFields />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="flex flex-col gap-6 lg:col-span-2">
            <Panel title="account.txt">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <Avatar
                    src={pageUser.avatar}
                    name={pageUser.display_name}
                    seed={pageUser.id}
                    size="lg"
                  />
                  <div className="min-w-0">
                    <p className="font-display text-subheading text-ink truncate font-bold">
                      {pageUser.display_name || pageUser.username}
                    </p>
                    <p className="text-caption text-ink-soft truncate">
                      @{pageUser.username}
                    </p>
                  </div>
                </div>

                {roles.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {roles.map((role) => (
                      <Badge key={role.label} tone={role.tone}>
                        {role.label}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="flex flex-col">
                  <DetailRow label="Email">{pageUser.email}</DetailRow>

                  <DetailRow label="Hack Club account">
                    {pageUser.account_id && missingFields.length === 0 ? (
                      <Badge tone="success">Linked</Badge>
                    ) : props.custom ? (
                      <Badge tone="warning">
                        {pageUser.account_id ? "Missing fields" : "Unlinked"}
                      </Badge>
                    ) : (
                      <ButtonAnchor
                        href="/auth/hca"
                        variant="secondary"
                        size="sm"
                      >
                        {pageUser.account_id
                          ? "Reauthenticate"
                          : "Link your account"}
                      </ButtonAnchor>
                    )}
                  </DetailRow>

                  {projectsOn && (
                    <DetailRow label="Hackatime">
                      {pageUser.hackatime_id ? (
                        <Badge tone="success">Linked</Badge>
                      ) : props.custom ? (
                        <Badge tone="warning">Unlinked</Badge>
                      ) : (
                        <ButtonLink
                          href="/hackatime/link"
                          variant="secondary"
                          size="sm"
                        >
                          Link Hackatime
                        </ButtonLink>
                      )}
                    </DetailRow>
                  )}
                </div>
              </div>
            </Panel>

            {/* The rail carries these too, but it's hidden below `lg` and this
                is where people look for help on a phone. */}
            {(props.program.support_channel_name ||
              props.program.support_email) && (
              <Panel title="help.txt">
                <div className="flex flex-col">
                  {props.program.support_channel_name && (
                    <DetailRow label="Ask in Slack">
                      <a
                        className="text-link underline"
                        href={props.program.support_channel_url ?? undefined}
                      >
                        #{props.program.support_channel_name}
                      </a>
                    </DetailRow>
                  )}

                  {props.program.support_email && (
                    <DetailRow label="Email us">
                      <a
                        className="text-link underline"
                        href={`mailto:${props.program.support_email}`}
                      >
                        {props.program.support_email}
                      </a>
                    </DetailRow>
                  )}
                </div>
              </Panel>
            )}

            {props.custom && props.user.is_admin && (
              <Panel title="permissions.form" tone="alt">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap gap-x-6 gap-y-3">
                    {PERMISSIONS.map((perm) => (
                      <label
                        key={perm}
                        title={PERMISSION_HINTS[perm]}
                        className="text-body text-ink flex cursor-pointer items-center gap-2"
                      >
                        <Checkbox
                          checked={permissions[perm]}
                          onChange={(e) =>
                            setPermissions((prev) => ({
                              ...prev,
                              [perm]: e.target.checked,
                            }))
                          }
                        />
                        {PERMISSION_LABELS[perm]}
                      </label>
                    ))}
                  </div>

                  <p className="text-caption text-ink-soft">
                    Travel grant lets this account open and ship projects with
                    no Boba Bash attached - for HQ staff coding towards travel
                    money. Clearing Onboarded replays the welcome tour the next
                    time they open the dashboard.
                  </p>

                  <Button
                    className="w-fit"
                    onClick={updatePermissions}
                    disabled={!permissionsChanged}
                  >
                    Save permissions
                  </Button>
                </div>
              </Panel>
            )}

            {economyOn && (
              <Panel title="tickets.txt">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <p className="font-display text-title text-carrot-orange font-bold">
                      {pageUser.balance}
                    </p>
                    <p className="text-caption text-ink-muted">
                      ticket{pageUser.balance === 1 ? "" : "s"}
                    </p>
                  </div>
                  {projectsOn && (
                    <>
                      <div>
                        <p className="font-display text-title text-ink font-bold">
                          {(pageUser.total_in_review_seconds / 3600).toFixed(1)}
                        </p>
                        <p className="text-caption text-ink-muted">
                          hours in review
                        </p>
                      </div>
                      <div>
                        <p className="font-display text-title text-leaf font-bold">
                          {(pageUser.total_approved_seconds / 3600).toFixed(1)}
                        </p>
                        <p className="text-caption text-ink-muted">
                          hours approved
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </Panel>
            )}

            {shopOn && props.orders.length > 0 && (
              <Panel title={`orders.list - ${props.orders.length}`}>
                <div className="flex flex-col gap-4">
                  {props.orders.map((order) => (
                    <OrderCard key={order.id} order={order} />
                  ))}
                </div>
              </Panel>
            )}

            {economyOn &&
              (pageUser.ticket_adjustments.length > 0 ||
                props.user.is_admin) && (
                <Panel title="adjustments.csv">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col">
                      {pageUser.ticket_adjustments.length === 0 && (
                        <p className="text-body text-ink-muted">
                          No adjustments yet.
                        </p>
                      )}

                      {pageUser.ticket_adjustments.map((adjustment) => (
                        <div
                          key={adjustment.id}
                          className="border-line/20 flex items-start justify-between gap-3 border-b py-3 last:border-b-0 last:pb-0"
                        >
                          <div className="min-w-0">
                            <p className="text-body text-ink font-bold">
                              <span
                                className={
                                  adjustment.amount > 0
                                    ? "text-leaf"
                                    : "text-sunset-orange"
                                }
                              >
                                {adjustment.amount > 0 && "+"}
                                {adjustment.amount}
                              </span>{" "}
                              - {adjustment.reason}
                            </p>
                            <p className="text-caption text-ink-soft">
                              {new Date(adjustment.created_at).toLocaleString()}
                            </p>
                          </div>

                          {props.user.is_admin && (
                            <ButtonLink
                              href={`/users/${pageUser.id}/adjustments/${adjustment.id}`}
                              method="delete"
                              as="button"
                              variant="danger"
                              size="sm"
                            >
                              Delete
                            </ButtonLink>
                          )}
                        </div>
                      ))}
                    </div>

                    {props.user.is_admin && (
                      <AdjustmentForm user_id={pageUser.id} />
                    )}
                  </div>
                </Panel>
              )}

            {economyOn && (
              <Panel title="transfers.csv">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col">
                    {transfers.length === 0 && (
                      <p className="text-body text-ink-muted">
                        No transfers yet.
                      </p>
                    )}

                    {transfers.map((transfer) => (
                      <div
                        key={transfer.id}
                        className="border-line/20 flex items-start justify-between gap-3 border-b py-3 last:border-b-0 last:pb-0"
                      >
                        <div className="min-w-0">
                          <p className="text-body text-ink">
                            <strong>{transfer.from_user_name}</strong>{" "}
                            {transfer.aasm_state === "pending"
                              ? "requested to transfer"
                              : "transferred"}{" "}
                            {transfer.amount} ticket
                            {transfer.amount === 1 ? "" : "s"} to{" "}
                            <strong>{transfer.to_user_name}</strong>
                          </p>
                          {transfer.reason && (
                            <p className="text-body text-ink-muted italic">
                              {transfer.reason}
                            </p>
                          )}
                          <p className="text-caption text-ink-soft">
                            {new Date(transfer.created_at).toLocaleString()}
                          </p>
                        </div>

                        {props.user.is_admin && (
                          <ButtonLink
                            href={`/users/${pageUser.id}/transfers/${transfer.id}`}
                            method="delete"
                            as="button"
                            variant="danger"
                            size="sm"
                          >
                            Delete
                          </ButtonLink>
                        )}
                      </div>
                    ))}
                  </div>

                  <TransferForm user_id={pageUser.id} />
                </div>
              </Panel>
            )}
          </div>

          <Panel title="timeline.log" tone="alt" className="h-fit">
            {props.timeline.length === 0 ? (
              <EmptyState
                title="Nothing here yet"
                description={
                  economyOn
                    ? "Ship a project and it'll show up here."
                    : "RSVP to an event and it'll show up here."
                }
              />
            ) : (
              <div className="flex flex-col">
                {props.timeline.map((item, index) => (
                  <div
                    key={`${item.date}-${index}`}
                    className="border-line/20 flex items-start gap-3 border-b py-3 last:border-b-0 last:pb-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-body text-ink font-bold">
                        {item.name}
                      </p>
                      <p className="text-caption text-ink-soft">
                        {new Date(item.date).toLocaleString()}
                      </p>
                    </div>

                    {item.link && (
                      <Link
                        href={item.link}
                        className="text-ink duration-fast ease-standard mt-1 transition-transform hover:scale-110"
                      >
                        <Icon
                          src={inviteIcon}
                          label="Open"
                          className="h-4 w-4"
                        />
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </Layout>
  );
}
